import { useEffect, useMemo, useState } from 'react';
import fieldWorkService from '../../services/fieldWork';
import {
  ACTIVITY_CATEGORY_LABELS,
  type ActivityCategory,
  type ActivityType,
} from '../../types/fieldWork';

interface ActivityTypePickerProps {
  value: number | null;
  /** The chosen type, or null for "not set" — handed back whole so the form can use its defaults. */
  onChange: (id: number | null, type: ActivityType | null) => void;
  id?: string;
}

const CATEGORY_ORDER = Object.keys(ACTIVITY_CATEGORY_LABELS) as ActivityCategory[];

/**
 * What kind of work a job is — picking, pruning, mowing … Built-in types plus
 * the org's own, grouped by category. Optional: a job without one is still a
 * job, it just cannot be costed by activity later.
 */
export default function ActivityTypePicker({ value, onChange, id = 'job-activity-type' }: ActivityTypePickerProps) {
  const [types, setTypes] = useState<ActivityType[]>([]);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let live = true;
    fieldWorkService.getActivityTypes()
      .then(t => { if (live) setTypes(t); })
      .catch(() => { if (live) setFailed(true); });
    return () => { live = false; };
  }, []);

  const grouped = useMemo(() => CATEGORY_ORDER
    .map(cat => ({ cat, items: types.filter(t => t.category === cat) }))
    .filter(g => g.items.length > 0), [types]);

  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-neutral-700 mb-1">
        Type of work
      </label>
      <select
        id={id}
        value={value ?? ''}
        onChange={e => {
          const next = e.target.value ? Number(e.target.value) : null;
          onChange(next, types.find(t => t.id === next) ?? null);
        }}
        className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-indigo-200"
      >
        <option value="">Not set</option>
        {grouped.map(g => (
          <optgroup key={g.cat} label={ACTIVITY_CATEGORY_LABELS[g.cat]}>
            {g.items.map(t => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </optgroup>
        ))}
      </select>
      {failed && (
        <p className="text-xs text-neutral-500 mt-1">Could not load the list of work types.</p>
      )}
    </div>
  );
}
